import React, { useState, useRef } from "react";
import {
    X,
    Upload,
    Save,
    Image as ImageIcon,
    Video as VideoIcon,
    AlertCircle,
    Info,
    Maximize2
} from "lucide-react";

export default function GalleryForm({ initialData = {}, onSubmit, isEditing = false }) {
    const [uploadType, setUploadType] = useState(initialData.youtube_link ? "youtube" : "file");
    const [formData, setFormData] = useState({
        type: initialData.media_type || "image",
        title: initialData.title || "",
        youtube_link: initialData.youtube_link || "",
        url: initialData.url || "",
        downloadable: initialData.downloadable !== false, // default to true if not specified
        ...initialData
    });
    const [file, setFile] = useState(null);
    const fileInputRef = useRef(null);
    const [previewUrl, setPreviewUrl] = useState(initialData.url || null);

    const getYouTubeID = (url) => {
        const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
        const match = url?.match(regExp);
        return (match && match[2].length === 11) ? match[2] : null;
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));

        if (name === "youtube_link") {
            const videoId = getYouTubeID(value);
            if (videoId) {
                setPreviewUrl(`https://www.youtube.com/embed/${videoId}?modestbranding=1&showinfo=0`);
            } else {
                setPreviewUrl(null);
            }
        }
    };

    const handleFileChange = (e) => {
        const selectedFile = e.target.files[0];
        if (selectedFile) {
            setFile(selectedFile);
            const objectUrl = URL.createObjectURL(selectedFile);
            setPreviewUrl(objectUrl);
        }
    };

    const handleTypeChange = (type) => {
        setFormData(prev => ({ ...prev, type }));
        if (type === "image") {
            setUploadType("file");
        }
    };

    const clearMedia = () => {
        if (uploadType === "file") {
            setFile(null);
            if (fileInputRef.current) fileInputRef.current.value = "";
            setPreviewUrl(isEditing && initialData.media_type === formData.type && !initialData.youtube_link ? initialData.url : null);
        } else {
            setFormData(prev => ({ ...prev, youtube_link: "" }));
            setPreviewUrl(isEditing && initialData.youtube_link && initialData.media_type === formData.type ? `https://www.youtube.com/embed/${getYouTubeID(initialData.youtube_link)}?modestbranding=1&showinfo=0` : null);
        }
    };

    return (
        <form onSubmit={(e) => { e.preventDefault(); onSubmit({ ...formData, file, uploadType }); }} className="space-y-8">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* Left Column: Media Info */}
                <div className="lg:col-span-2 space-y-6">
                    <div className="bg-white dark:bg-[#1A1A1A] p-6 lg:p-8 rounded-2xl border border-black/5 dark:border-white/5 shadow-sm">
                        <h2 className="text-xl font-bold mb-6 flex items-center gap-2" style={{ fontFamily: "Playfair Display, serif" }}>
                            <ImageIcon size={20} className="text-jazz-brown" /> Media Information
                        </h2>

                        <div className="space-y-6">
                            {/* Type Selection */}
                            <div className="flex gap-4 p-1 bg-gray-100 dark:bg-zinc-800 rounded-xl w-fit">
                                <button
                                    type="button"
                                    onClick={() => handleTypeChange("image")}
                                    className={`flex items-center gap-2 px-6 py-2 rounded-lg text-xs font-bold tracking-wider transition-all ${formData.type === "image" ? "bg-white dark:bg-[#1A1A1A] text-black dark:text-white shadow-sm" : "text-black dark:text-white/50"}`}
                                    disabled={isEditing}
                                >
                                    <ImageIcon size={14} /> IMAGE
                                </button>
                                <button
                                    type="button"
                                    onClick={() => handleTypeChange("video")}
                                    className={`flex items-center gap-2 px-6 py-2 rounded-lg text-xs font-bold tracking-wider transition-all ${formData.type === "video" ? "bg-white dark:bg-[#1A1A1A] text-black dark:text-white shadow-sm" : "text-black dark:text-white/50"}`}
                                    disabled={isEditing}
                                >
                                    <VideoIcon size={14} /> VIDEO
                                </button>
                            </div>

                            <div>
                                <label className="block text-xs font-bold text-black dark:text-white/60 uppercase tracking-widest mb-2">Title / Caption</label>
                                <input
                                    type="text"
                                    name="title"
                                    value={formData.title}
                                    onChange={handleChange}
                                    placeholder="e.g., Live at Blue Note NYC"
                                    className="w-full px-4 py-3 rounded-xl border border-black/5 bg-[#FAFAFA] dark:bg-[#222] focus:outline-none focus:ring-2 focus:ring-jazz-brown/50 text-sm font-medium"
                                    required
                                />
                            </div>

                            {/* Video Upload Type Toggle */}
                            {formData.type === "video" && (
                                <div className="space-y-4">
                                    <label className="block text-xs font-bold text-black dark:text-white/60 uppercase tracking-widest px-1">Upload Method</label>
                                    <div className="flex gap-3">
                                        <button
                                            type="button"
                                            onClick={() => { setUploadType("file"); setPreviewUrl(file ? URL.createObjectURL(file) : (initialData.media_type === "video" ? initialData.url : null)); }}
                                            className={`flex-1 py-3 px-4 rounded-xl border text-xs font-bold tracking-widest uppercase transition-all ${uploadType === "file" ? "bg-black text-white border-black" : "bg-white border-black/5 text-black dark:text-white/50"}`}
                                        >
                                            Media File
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setUploadType("youtube");
                                                setFormData(prev => ({ ...prev, downloadable: false }));
                                                setPreviewUrl(getYouTubeID(formData.youtube_link) ? `https://www.youtube.com/embed/${getYouTubeID(formData.youtube_link)}` : null);
                                            }}
                                            className={`flex-1 py-3 px-4 rounded-xl border text-xs font-bold tracking-widest uppercase transition-all ${uploadType === "youtube" ? "bg-black text-white border-black" : "bg-white border-black/5 text-black dark:text-white/50"}`}
                                        >
                                            YouTube Link
                                        </button>
                                    </div>
                                </div>
                            )}

                            <div>
                                <label className="block text-xs font-bold text-black dark:text-white/60 uppercase tracking-widest mb-2">
                                    {uploadType === "file" ? (isEditing ? "Replace Media (Optional)" : "Upload Media") : "YouTube Video Link"}
                                </label>

                                {uploadType === "file" ? (
                                    <div className="space-y-6">
                                        <div className="relative">
                                            <div
                                                onClick={() => fileInputRef.current?.click()}
                                                className={`border-2 border-dashed ${file ? 'border-jazz-brown bg-jazz-brown/5' : 'border-black/10 dark:border-white/10 bg-[#FAFAFA] dark:bg-[#222]'} rounded-2xl p-8 text-center cursor-pointer hover:border-jazz-brown/50 transition-colors`}
                                            >
                                                <input
                                                    type="file"
                                                    ref={fileInputRef}
                                                    onChange={handleFileChange}
                                                    className="hidden"
                                                    accept={formData.type === "image" ? "image/*" : "video/*"}
                                                />
                                                <Upload className={`mx-auto mb-3 ${file ? 'text-jazz-brown' : 'text-black/40'}`} size={32} />
                                                <p className={`text-sm font-medium ${file ? 'text-jazz-brown' : 'text-black dark:text-white/60'}`}>
                                                    {file ? file.name : (isEditing ? "Click to replace current file" : "Click to select file")}
                                                </p>
                                                <p className="text-xs text-black/60 mt-1 uppercase tracking-wider">
                                                    {formData.type === "image" ? "JPG, PNG up to 10MB" : "MP4 up to 50MB"}
                                                </p>
                                            </div>
                                            {file && (
                                                <button
                                                    type="button"
                                                    onClick={(e) => { e.stopPropagation(); clearMedia(); }}
                                                    className="absolute -top-3 -right-3 w-8 h-8 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 hover:scale-110 transition-all shadow-lg border-2 border-white"
                                                    title="Remove File"
                                                >
                                                    <X size={14} strokeWidth={3} />
                                                </button>
                                            )}
                                        </div>

                                        {formData.type === "video" && (
                                            <div className="p-4 bg-jazz-brown/5 border border-jazz-brown/20 rounded-xl">
                                                <p className="text-xs text-jazz-brown leading-relaxed uppercase tracking-widest font-bold flex items-center gap-2">
                                                    <Info size={14} /> A preview will be auto-generated from your video.
                                                </p>
                                            </div>
                                        )}
                                    </div>
                                ) : (
                                    <div className="relative">
                                        <input
                                            type="url"
                                            name="youtube_link"
                                            value={formData.youtube_link}
                                            onChange={handleChange}
                                            placeholder="https://www.youtube.com/watch?v=..."
                                            className={`w-full px-4 py-3 pr-12 rounded-xl border ${formData.youtube_link ? 'border-jazz-brown bg-jazz-brown/5' : 'border-black/5 bg-[#FAFAFA] dark:bg-[#222]'} focus:outline-none focus:ring-2 focus:ring-jazz-brown/50 text-sm font-medium transition-colors`}
                                            required={uploadType === "youtube" && !isEditing}
                                        />
                                        {formData.youtube_link && (
                                            <button
                                                type="button"
                                                onClick={(e) => { e.stopPropagation(); clearMedia(); }}
                                                className="absolute top-1/2 -translate-y-1/2 right-3 w-6 h-6 bg-red-500/10 text-red-500 rounded-full flex items-center justify-center hover:bg-red-500 hover:text-white transition-all"
                                                title="Clear Link"
                                            >
                                                <X size={14} />
                                            </button>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Preview Section */}
                    {previewUrl && (
                        <div className="bg-white dark:bg-[#1A1A1A] p-6 lg:p-8 rounded-2xl border border-black/5 dark:border-white/5 shadow-sm">
                            <h2 className="text-xl font-bold mb-6 flex items-center justify-between" style={{ fontFamily: "Playfair Display, serif" }}>
                                <div className="flex items-center gap-2">
                                    <Maximize2 size={20} className="text-jazz-brown" /> Media Preview
                                </div>
                                <button
                                    type="button"
                                    onClick={clearMedia}
                                    className="text-xs font-bold tracking-widest uppercase text-red-500 hover:text-red-600 flex items-center gap-1 transition-colors"
                                >
                                    <X size={14} /> Clear Preview
                                </button>
                            </h2>
                            <div className="aspect-video bg-gray-100 dark:bg-zinc-800 rounded-xl overflow-hidden border border-black/5 flex items-center justify-center">
                                {formData.type === "image" ? (
                                    <img
                                        src={previewUrl}
                                        alt="Preview"
                                        className="w-full h-full object-contain"
                                        onError={(e) => { e.target.src = "https://via.placeholder.com/800x450?text=Invalid+Media+URL"; }}
                                    />
                                ) : (
                                    uploadType === "youtube" ? (
                                        <iframe
                                            src={previewUrl}
                                            className="w-full h-full"
                                            title="YouTube Preview"
                                            frameBorder="0"
                                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                            allowFullScreen
                                        />
                                    ) : (
                                        <video src={previewUrl} controls className="w-full h-full object-contain" />
                                    )
                                )}
                            </div>
                        </div>
                    )}
                </div>

                {/* Right Column: Actions & Settings */}
                <div className="space-y-6">
                    <div className="bg-white dark:bg-[#1A1A1A] p-6 lg:p-8 rounded-2xl border border-black/5 dark:border-white/5 shadow-sm">
                        <h3 className="text-lg font-bold mb-6" style={{ fontFamily: "Playfair Display, serif" }}>
                            Settings & Visibility
                        </h3>
                        <div className="space-y-4">
                            <div className="flex items-center justify-between p-4 rounded-xl border border-black/5 bg-[#FAFAFA] dark:bg-[#222]">
                                <div>
                                    <p className="text-xs font-bold text-black uppercase tracking-widest">Type</p>
                                    <p className="text-xs text-black dark:text-white/50 uppercase tracking-widest mt-0.5">{formData.type}</p>
                                </div>
                            </div>

                            <div className="flex items-center justify-between p-4 rounded-xl border border-black/5 bg-[#FAFAFA] dark:bg-[#222]">
                                <div>
                                    <p className="text-xs font-bold text-black uppercase tracking-widest">Downloadable</p>
                                    <p className="text-[10px] text-black/50 dark:text-white/40 uppercase tracking-widest mt-0.5">Include in Media Kit</p>
                                </div>
                                <label className={`relative inline-flex items-center ${uploadType === "youtube" ? "cursor-not-allowed opacity-50" : "cursor-pointer"}`}>
                                    <input
                                        type="checkbox"
                                        className="sr-only peer"
                                        checked={formData.downloadable}
                                        onChange={(e) => setFormData(prev => ({ ...prev, downloadable: e.target.checked }))}
                                        disabled={uploadType === "youtube"}
                                    />
                                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer dark:bg-zinc-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-jazz-brown"></div>
                                </label>
                            </div>
                            {uploadType === "youtube" && (
                                <p className="text-[10px] text-jazz-brown font-bold uppercase tracking-widest px-1 text-right">
                                    Not available for YouTube links
                                </p>
                            )}
                        </div>
                    </div>

                    <div className="bg-black text-white p-6 lg:p-8 rounded-2xl shadow-xl relative overflow-hidden">
                        <div className="absolute top-0 right-0 w-32 h-32 bg-jazz-brown/20 rounded-full blur-3xl translate-x-1/2 -translate-y-1/2" />
                        <h3 className="text-lg font-bold mb-4 relative z-10" style={{ fontFamily: "Playfair Display, serif" }}>
                            Media Notes
                        </h3>
                        <div className="space-y-4 relative z-10">
                            <div className="flex gap-3">
                                <Info size={16} className="text-jazz-brown flex-shrink-0 mt-0.5" />
                                <p className="text-xs text-white leading-relaxed uppercase tracking-widest">
                                    Images will be displayed with a slight artistic rotation in the gallery view.
                                </p>
                            </div>
                            <div className="flex gap-3">
                                <AlertCircle size={16} className="text-jazz-brown flex-shrink-0 mt-0.5" />
                                <p className="text-xs text-white leading-relaxed uppercase tracking-widest">
                                    {uploadType === "youtube" ? "YouTube videos will be embedded directly for app viewing." : "For videos, uploading will start processing in the background."}
                                </p>
                            </div>
                        </div>
                    </div>

                    <div className="space-y-3">
                        <button
                            type="submit"
                            className="w-full flex items-center justify-center gap-2 bg-black text-white py-4 rounded-xl text-sm font-bold tracking-widest hover:bg-jazz-brown transition-all shadow-xl shadow-black/10"
                            disabled={!isEditing && (uploadType === "file" ? !file : !formData.youtube_link)}
                        >
                            <Save size={18} /> {isEditing ? "UPDATE MEDIA" : "PUBLISH TO GALLERY"}
                        </button>
                        <button
                            type="button"
                            onClick={() => window.history.back()}
                            className="w-full py-4 rounded-xl text-xs font-bold tracking-widest text-black dark:text-white/60 hover:text-black/80 dark:hover:text-white border border-black/5 bg-white dark:bg-[#1A1A1A] transition-all"
                        >
                            CANCEL
                        </button>
                    </div>
                </div>
            </div>
        </form>
    );
}
